'use client';
import React from 'react';
import { X, Settings, LogOut } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { createClient } from '../lib/supabase/client';
import { useSession } from '../contexts/SessionContext';

interface UserConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  inboundEnabled: boolean;
  onToggleInbound: (value: boolean) => void;
}

export default function UserConfigModal({ isOpen, onClose, inboundEnabled, onToggleInbound }: UserConfigModalProps) {
  if (!isOpen) return null;

  const router = useRouter();
  const { clearSession } = useSession();

  async function handleLogout() {
    const supabase = createClient();
    await supabase.auth.signOut();
    clearSession();
    onClose();
    router.push('/');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-start" onClick={onClose}>
      <div
        className="mb-20 ml-4 w-72 bg-card border border-border rounded-xl shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings size={15} className="text-muted-foreground" />
            <span className="text-sm font-semibold text-foreground">Configuration</span>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>

        {/* User info */}
        <div className="flex items-center gap-3 mb-5 pb-4 border-b border-border">
          <div className="w-9 h-9 rounded-full bg-primary/30 flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">
            AM
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">Alex Morgan</p>
            <p className="text-xs text-muted-foreground truncate">alex@mediavault.com</p>
          </div>
        </div>

        {/* Menu Sections */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Menu Sections</p>

          {/* Inbound toggle */}
          <div className="flex items-center justify-between py-2.5">
            <div>
              <p className="text-sm font-medium text-foreground">Inbound</p>
              <p className="text-xs text-muted-foreground">Media Planner, AI Sales Agent, RFP Engine</p>
            </div>
            <button
              role="switch"
              aria-checked={inboundEnabled}
              onClick={() => onToggleInbound(!inboundEnabled)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                inboundEnabled ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${
                  inboundEnabled ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Log Out */}
        <div className="mt-4 pt-4 border-t border-border">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <LogOut size={15} />
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}
